import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Search, LayoutTemplate, Save, PlayCircle, Globe, Lock, FolderPlus, Clapperboard,
  ExternalLink, History, RefreshCw, PencilLine,
} from 'lucide-react';
import { toast } from 'sonner';
import { loadTemplates, saveTemplates } from '@/components/project-templates/ProjectTemplateLibraryDemo';
import {
  getTemplatesByType, entriesOf, TEMPLATE_KIND_TO_TYPE, getProjectDefaultTemplate, getProjectAlternateTemplates,
  ensureEpisodeSnapshot, isEpisodeTemplateStale, reapplyLatestTemplate, selectEpisodeTemplate,
} from '@/lib/templateFolders';

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

// Consistent, attractive placeholder thumbnail per Editing Template —
// no image generation involved, just a deterministic gradient keyed off
// the template's own id/name so the same template always looks the same.
const THUMB_GRADIENTS = [
  'from-[#3A7BFF] to-[#1C3F8F]', 'from-[#FF6B9D] to-[#8F1C4F]',
  'from-[#22C55E] to-[#0F5C2E]', 'from-[#F59E0B] to-[#8A4E05]',
  'from-[#A855F7] to-[#4C1D7A]', 'from-[#06B6D4] to-[#0A4F5C]',
];
function thumbGradient(key) {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return THUMB_GRADIENTS[hash % THUMB_GRADIENTS.length];
}

// Template Folder (section 3 terminology) is a reusable container; each
// one can hold multiple templates of different types — see
// `lib/templateFolders.js` for the shared type-scoping contract.

/**
 * Templates category (sidebar-driven). Extends — never replaces — the
 * existing Template Folder system shared with the central Templates
 * Production Tool (same `consistency_demo_templates_{creatorId}`
 * localStorage records). Level 1/2 only: "Apply" and "Save as Template"
 * are demo actions here — no backend persistence, marketplace API, or
 * team-sharing logic.
 */
export function EditorTemplatesPanel({ creatorId, templateKind = 'Editing Template', buildTemplatePayload, onApply, filterTemplate, applyHint, projectId, episodeId }) {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: '', target: 'new', newFolderName: '', visibility: 'private' });
  const type = TEMPLATE_KIND_TO_TYPE[templateKind] || 'editing';

  useEffect(() => { if (creatorId) setTemplates(loadTemplates(creatorId)); }, [creatorId]);

  // Project/episode inheritance (sections 10–12) — only activates when a
  // caller passes `projectId`; purely additive, every existing caller
  // that doesn't pass it sees no change at all.
  const [inherited, setInherited] = useState(null); // { folderId, folderName, templateId, templateName, snapshot, customized, isProjectDefault }
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    if (!creatorId || !projectId) { setInherited(null); return; }
    if (episodeId) { setInherited(ensureEpisodeSnapshot(creatorId, projectId, episodeId, type)); return; }
    const def = getProjectDefaultTemplate(creatorId, projectId, type);
    setInherited(def ? { folderId: def.folder.id, folderName: def.folder.name, templateId: def.entry.id, templateName: def.entry.name, isProjectDefault: true } : null);
  }, [creatorId, projectId, episodeId, type, refreshTick]);
  const alternates = useMemo(() => (creatorId && projectId ? getProjectAlternateTemplates(creatorId, projectId, type) : []), [creatorId, projectId, type, refreshTick]);
  const stale = episodeId && inherited ? isEpisodeTemplateStale(creatorId, inherited) : false;

  const filtered = useMemo(() => {
    const byType = templates.filter((t) => getTemplatesByType(t, type).length > 0);
    const q = search.trim().toLowerCase();
    if (!q) return byType;
    return byType.filter((t) => (t.name || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q));
  }, [templates, search, type]);

  const openSave = () => { setSaveForm({ name: '', target: templates[0]?.id || 'new', newFolderName: '', visibility: 'private' }); setSaveOpen(true); };

  const handleApply = (tpl, editTpl) => {
    if (projectId && episodeId) setInherited(selectEpisodeTemplate(projectId, episodeId, type, tpl, editTpl));
    if (onApply) { onApply(tpl, editTpl); return; }
    toast.info(`Applied "${editTpl.name}" from "${tpl.name}" (demo) — blueprint noted, no timeline changes made. Your existing work is untouched.`);
  };

  const handleReapplyLatest = () => {
    const next = reapplyLatestTemplate(creatorId, projectId, episodeId, type);
    if (!next) return;
    setInherited(next);
    const folder = templates.find((t) => t.id === next.folderId);
    const entry = folder && entriesOf(folder).find((et) => et.id === next.templateId);
    if (folder && entry && onApply) onApply(folder, entry);
    toast.success('Reapplied the latest version of this template to this episode.');
  };

  const handleSave = () => {
    if (!saveForm.name.trim()) { toast.error(`Give this ${templateKind.toLowerCase()} a name first`); return; }
    const newEditTpl = {
      id: uid(), name: saveForm.name.trim(), segments: [], outputs: [], created_at: new Date().toISOString(), templateType: type,
      ...(buildTemplatePayload ? buildTemplatePayload(saveForm.name.trim()) : {}),
    };

    if (saveForm.target === 'new') {
      if (!saveForm.newFolderName.trim()) { toast.error('Give the new Template Folder a name first'); return; }
      const folder = {
        id: uid(), name: saveForm.newFolderName.trim(), description: 'Created from a Production Tool',
        content_type: 'Custom', series_name: '', segments: [], outputs: [], use_brand_kit_defaults: false, owner: 'you',
        visibility: saveForm.visibility, editing_templates: [newEditTpl], updated_at: new Date().toISOString(), created_at: new Date().toISOString(),
      };
      const next = [folder, ...templates];
      setTemplates(next); saveTemplates(creatorId, next);
      toast.success(`Saved as a new Template Folder "${folder.name}"`);
    } else {
      const next = templates.map((t) => (t.id === saveForm.target
        ? { ...t, editing_templates: [...entriesOf(t), newEditTpl], visibility: saveForm.visibility, updated_at: new Date().toISOString() }
        : t));
      setTemplates(next); saveTemplates(creatorId, next);
      toast.success(`Saved into "${templates.find((t) => t.id === saveForm.target)?.name}"`);
    }
    setSaveOpen(false);
    setRefreshTick((v) => v + 1);
  };

  return (
    <div className="h-full flex flex-col" data-testid="editor-templates-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] uppercase tracking-wide text-[#555E6E]">Templates</p>
        {projectId && (
          <Link to={`/app/create/templates?projectId=${projectId}&creatorId=${creatorId || ''}`} className="text-[10px] text-[#3A7BFF] hover:text-white flex items-center gap-1" data-testid="templates-open-library-link">
            Open Templates Library<ExternalLink className="h-2.5 w-2.5" />
          </Link>
        )}
      </div>

      {inherited && (
        <div className="mb-3 p-2.5 rounded-lg border border-[#3A7BFF]/30 bg-[#3A7BFF]/5" data-testid="templates-inherited-banner">
          <p className="text-[10px] text-[#8A93A6]">{inherited.isProjectDefault ? 'Project default' : 'This episode is using'}</p>
          <p className="text-xs text-white font-medium truncate">{inherited.templateName} <span className="text-[#555E6E] font-normal">from {inherited.folderName}</span></p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {inherited.customized && <Badge variant="outline" className="border-amber-500/30 text-amber-300 text-[9px] gap-1"><PencilLine className="h-2.5 w-2.5" />Customized this episode</Badge>}
            {stale && (
              <Button size="sm" variant="outline" className="h-6 text-[10px] border-amber-500/30 text-amber-300 px-2" onClick={handleReapplyLatest} data-testid="templates-reapply-latest-button">
                <RefreshCw className="h-2.5 w-2.5 mr-1" />Template updated — Reapply Latest
              </Button>
            )}
            {alternates.length > 1 && <span className="text-[10px] text-[#555E6E] flex items-center gap-1"><History className="h-2.5 w-2.5" />{alternates.length} alternates below</span>}
          </div>
        </div>
      )}

      <div className="relative mb-3">
        <Search className="h-3.5 w-3.5 text-[#8A93A6] absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search Template Folders" className="h-8 pl-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="templates-search-input" />
      </div>

      <Button size="sm" variant="outline" className="w-full border-white/10 justify-center mb-3" onClick={openSave} data-testid="templates-save-as-button">
        <Save className="h-3.5 w-3.5 mr-1.5" />Save {templateKind}
      </Button>

      <p className="text-[11px] text-[#555E6E] mb-2">
        <span className="text-white font-medium">Template Folders</span> are shared with the Templates Production Tool. Each folder can hold multiple <span className="text-white font-medium">{templateKind}s</span> you apply or save below.
      </p>

      <div className="space-y-3 overflow-y-auto flex-1 pr-1" data-testid="templates-folder-list">
        {filtered.map((tpl) => (
          <div key={tpl.id} className="rounded-lg border border-white/10 bg-white/[0.03] overflow-hidden" data-testid={`templates-folder-${tpl.id}`}>
            <div className="flex items-center justify-between gap-2 px-2.5 py-2 bg-white/[0.04] border-b border-white/5">
              <span className="flex items-center gap-1.5 text-xs font-semibold text-white truncate" title="Template Folder">
                <LayoutTemplate className="h-3.5 w-3.5 text-[#3A7BFF] shrink-0" />{tpl.name}
              </span>
              <Badge variant="outline" className={`text-[9px] shrink-0 gap-1 ${tpl.visibility === 'public' ? 'border-green-500/30 text-green-300 bg-green-500/10' : 'border-white/10 text-[#8A93A6]'}`}>
                {tpl.visibility === 'public' ? <Globe className="h-2.5 w-2.5" /> : <Lock className="h-2.5 w-2.5" />}
                {tpl.visibility === 'public' ? 'Public' : 'Private'}
              </Badge>
            </div>
            <div className="p-2 space-y-1.5">
              {getTemplatesByType(tpl, type).filter(filterTemplate || (() => true)).map((et) => (
                <div key={et.id} className={`flex items-center gap-2 bg-black/20 rounded-md p-1.5 ${inherited?.templateId === et.id ? 'ring-1 ring-[#3A7BFF]/50' : ''}`} data-testid={`templates-editing-template-${et.id}`}>
                  <div className={`h-8 w-12 rounded shrink-0 bg-gradient-to-br ${thumbGradient(et.id)} flex items-center justify-center overflow-hidden`} data-testid={`templates-editing-template-thumb-${et.id}`}>
                    {et.design?.previewUrl ? <img src={et.design.previewUrl} alt="" className="h-full w-full object-cover" /> : <Clapperboard className="h-3.5 w-3.5 text-white/80" />}
                  </div>
                  <span className="flex-1 min-w-0 text-[11px] text-[#C0C7D4] truncate" title={templateKind}>{et.name}</span>
                  <button onClick={() => handleApply(tpl, et)} className="shrink-0 flex items-center gap-1 text-[10px] text-[#3A7BFF] hover:text-white" data-testid={`templates-apply-${et.id}`}>
                    <PlayCircle className="h-3 w-3" />Apply
                  </button>
                </div>
              ))}
              {getTemplatesByType(tpl, type).filter(filterTemplate || (() => true)).length === 0 && (
                <p className="text-[10px] text-[#555E6E] text-center py-2">No {templateKind}s in this folder yet.</p>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-8" data-testid="templates-folder-list-empty">
            <FolderPlus className="h-6 w-6 text-[#3A3F4B] mx-auto mb-2" />
            <p className="text-xs text-[#8A93A6]">{templates.length === 0 ? 'No Template Folders yet.' : `No ${templateKind}s match your search.`}</p>
            <p className="text-[11px] text-[#555E6E] mt-0.5">{applyHint || 'You can start now and save your work as a template later.'}</p>
          </div>
        )}
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-[#14171F] border-white/10 text-white sm:max-w-md" data-testid="templates-save-dialog">
          <DialogHeader><DialogTitle className="text-white">Save as {templateKind}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] text-[#8A93A6]">{templateKind} name</Label>
              <Input value={saveForm.name} onChange={(e) => setSaveForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Weekly Episode Cut" className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="templates-save-name-input" />
            </div>
            <div>
              <Label className="text-[11px] text-[#8A93A6]">Save into</Label>
              <Select value={saveForm.target} onValueChange={(v) => setSaveForm((f) => ({ ...f, target: v }))}>
                <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="templates-save-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">+ New Template Folder</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {saveForm.target === 'new' && (
              <div>
                <Label className="text-[11px] text-[#8A93A6]">New Template Folder name</Label>
                <Input value={saveForm.newFolderName} onChange={(e) => setSaveForm((f) => ({ ...f, newFolderName: e.target.value }))} placeholder="e.g. Weekly Podcast Rollout" className="h-8 text-xs bg-black/40 border-white/10 mt-1" data-testid="templates-save-folder-input" />
              </div>
            )}
            <div className="flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-2">
                {saveForm.visibility === 'public' ? <Globe className="h-4 w-4 text-green-400" /> : <Lock className="h-4 w-4 text-[#8A93A6]" />}
                <div>
                  <p className="text-xs text-white">{saveForm.visibility === 'public' ? 'Public' : 'Private'}</p>
                  <p className="text-[10px] text-[#555E6E]">{saveForm.visibility === 'public' ? 'Visible to your other creator profiles in this browser — no platform-wide listing yet' : 'Only visible to you'}</p>
                </div>
              </div>
              <Switch checked={saveForm.visibility === 'public'} onCheckedChange={(v) => setSaveForm((f) => ({ ...f, visibility: v ? 'public' : 'private' }))} data-testid="templates-save-visibility-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={handleSave} data-testid="templates-save-confirm-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
